/**
 * « MES ÉLÈVES », PAR « POURQUOI MAINTENANT » — chantier « manuel vivant »,
 * Φ-MV-5 (MV-TUT-01, F-11, F-27).
 *
 * Une lecture : `GET /v1/tuteur/manuel/eleves` (`ElevesSuivisResponse`). Le
 * serveur pèse sept signaux datés, ORDONNE les élèves et écrit la raison de
 * chacun en une phrase. Cet écran ne porte AUCUNE règle de priorité : il rend
 * l'ordre reçu, la phrase reçue, les étiquettes reçues. Un filtre ou une
 * recherche retiennent des lignes ; ils ne les réordonnent pas.
 *
 * ─── La charge (F-11, MV-UX-02) ─────────────────────────────────────────────
 *
 * Ouvert : le tableau, et lui seul. Repliés, chacun avec un résumé qui dit ce
 * qu'il y a derrière : les filtres par motif, « Mes prochaines séances »,
 * « Ajouter un élève », et la note qui explique l'ordre.
 *
 * ─── Sur tablette (revue UX `UX-7-11`) ──────────────────────────────────────
 *
 * Sept colonnes faisaient 1 067 px dans un cadre de 800 (iPad en portrait) ou
 * de 916 (en paysage) : « Fiche » était hors champ, sans indice de défilement.
 * Sous 1 200 px, « Sept derniers jours » et « Prochaine séance » se replient en
 * UNE LIGNE GRISE sous la raison (`.cellule-raison__repli`) : restent cinq
 * colonnes, qui tiennent. Et LA LIGNE ENTIÈRE mène à la fiche — le prénom et
 * « Fiche » restent des liens, pour le clavier et le lecteur d'écran.
 *
 * ─── Une ligne sans donnée pédagogique ──────────────────────────────────────
 *
 * Un élève rattaché dont le parent n'a pas donné le consentement arrive SANS
 * raison, sans compte, sans évaluation (`lireLigne`). La ligne le dit
 * sobrement, et n'offre aucun lien vers une fiche que le serveur refuserait.
 *
 * ─── Ce que cet écran ne montre pas ─────────────────────────────────────────
 *
 * Ni école, ni groupe, ni « Tous les élèves » d'une responsable (F-27 :
 * l'agence est un autre chantier). Aucun pourcentage : la préparation est un
 * ÉTAT, les sept derniers jours sont deux COMPTES. Le calcul de l'ordre n'est
 * pas rendu par le serveur, donc pas affiché — seule sa règle l'est, en clair.
 */
import {
  denombrer,
  type ElevesSuivisResponse,
  type EtatPreparation,
  type SignalDePriorite,
} from '@silex/shared';
import { useMemo, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { Badge } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { Chargement, ErreurRessource } from '../../components/EtatRessource';
import { SectionRabattable } from '../../components/SectionRabattable';
import { adressesManuelEleve, dansCombien, jourCivil } from '../../lib/manuel-tuteur-format';
import {
  PHRASE_SUIVI_NON_AUTORISE,
  direJour,
  direSeptJours,
  lireLigne,
  prochainesSeances,
  retenirLignes,
  type LigneDeListe,
} from '../../lib/manuel-tuteur-lectures';
import type { BadgeTone } from '../../lib/suivi-format';
import { formatDateHeure, initiale } from '../../lib/tuteur-format';
import { useAsyncRechargeable } from '../../lib/useAsync';

/** L'état d'une préparation → le ton de sa pastille. Jamais de rouge : « à renforcer » est en ardoise. */
const TON_PREPARATION: Readonly<Record<EtatPreparation, BadgeTone>> = {
  pret: 'acquis',
  en_bonne_voie: 'en-cours',
  a_renforcer: 'a-revoir',
  a_commencer: 'a-revoir',
};

export function ElevesParUrgence({
  ajout,
}: {
  /** « Ajouter un élève » — le formulaire de la page ; il reçoit de quoi relire la liste. */
  readonly ajout: (surRattache: () => void) => ReactNode;
}) {
  const { resource, recharger } = useAsyncRechargeable(
    useMemo(() => () => api.manuelElevesSuivis(), []),
  );

  return (
    <div className="content__inner manuel-tuteur">
      {resource.status === 'loading' ? (
        <>
          <div className="page-head">
            <h1>Mes élèves</h1>
          </div>
          <Chargement quoi="vos élèves" />
        </>
      ) : null}
      {resource.status === 'error' ? (
        <>
          <div className="page-head">
            <h1>Mes élèves</h1>
          </div>
          <ErreurRessource message={resource.message} />
        </>
      ) : null}
      {resource.status === 'ready' ? (
        <ListeParUrgence reponse={resource.data} ajout={ajout(recharger)} />
      ) : null}
    </div>
  );
}

/** La liste elle-même, une fois lue : sans appel, elle s'éprouve par son rendu. */
export function ListeParUrgence({
  reponse,
  ajout,
}: {
  readonly reponse: ElevesSuivisResponse;
  readonly ajout: ReactNode;
}) {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const signal = reponse.parSignal.find((s) => s.code === params.get('signal'))?.code ?? null;

  const lignes = useMemo(() => reponse.eleves.map(lireLigne), [reponse]);
  const visibles = retenirLignes(lignes, signal, q);
  const seances = prochainesSeances(lignes);
  const filtreActif = reponse.parSignal.find((s) => s.code === signal);

  const regler = (cle: 'q' | 'signal', valeur: string): void => {
    const suite = new URLSearchParams(params);
    if (valeur === '') suite.delete(cle);
    else suite.set(cle, valeur);
    setParams(suite, { replace: true });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 data-testid="eleves-urgence-titre">Mes élèves</h1>
          <p className="page-sub" data-testid="eleves-urgence-compte">
            {jourCivil(reponse.aujourdhui)} · {denombrer(reponse.eleves.length, 'élève')} ·{' '}
            {reponse.aVoirEnPriorite} à voir en priorité cette semaine
          </p>
        </div>
      </div>

      <div className="filters">
        <label className="field field--inline">
          <span className="lbl sr-only">Chercher un élève</span>
          <input
            className="control"
            placeholder="Chercher un élève"
            value={q}
            onChange={(e) => regler('q', e.target.value)}
            data-testid="eleves-urgence-recherche"
          />
        </label>
      </div>

      <div className="rabat-avant-tableau" data-testid="eleves-urgence-filtres">
        <SectionRabattable
          variante="tuile"
          titre="Filtrer par motif"
          resume={
            filtreActif === undefined
              ? 'Tous les élèves'
              : `${filtreActif.libelle} · ${denombrer(filtreActif.eleves, 'élève')}`
          }
          ouverteParDefaut={false}
        >
          <div className="filters" role="group" aria-label="Motif">
            <FiltreDeSignal
              libelle="Tous"
              eleves={reponse.eleves.length}
              actif={signal === null}
              surChoix={() => regler('signal', '')}
              code={null}
            />
            {reponse.parSignal.map((s) => (
              <FiltreDeSignal
                key={s.code}
                libelle={s.libelle}
                eleves={s.eleves}
                actif={s.code === signal}
                surChoix={() => regler('signal', s.code)}
                code={s.code}
              />
            ))}
          </div>
        </SectionRabattable>
      </div>

      {reponse.eleves.length === 0 ? (
        <EmptyState title="Vous ne suivez aucun élève">
          Un élève rejoint votre suivi par le code qu’il affiche dans son application.
        </EmptyState>
      ) : null}
      {reponse.eleves.length > 0 && visibles.length === 0 ? (
        <EmptyState title="Aucun élève ne correspond.">
          Effacez la recherche ou le filtre pour les revoir tous.
        </EmptyState>
      ) : null}

      {visibles.length > 0 ? (
        <div className="table-wrap" data-testid="eleves-urgence-tableau">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Ordre</th>
                <th scope="col">Élève</th>
                <th scope="col">Pourquoi maintenant</th>
                <th scope="col">Prochaine évaluation</th>
                <th scope="col" className="col-large">
                  Sept derniers jours
                </th>
                <th scope="col" className="col-large">
                  Prochaine séance
                </th>
                <th scope="col">
                  <span className="sr-only">Ouvrir la fiche</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((l) =>
                l.suivi ? (
                  <LigneSuivie key={l.eleve.id} ligne={l} />
                ) : (
                  <LigneSansSuivi key={l.eleve.id} ligne={l} />
                ),
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="stack" data-testid="eleves-urgence-annexes">
        <div data-testid="eleves-urgence-seances">
          <SectionRabattable
            titre="Mes prochaines séances"
            resume={
              seances.length === 0
                ? 'Aucune séance planifiée'
                : `${denombrer(seances.length, 'séance')} · la prochaine : ${seances[0]?.eleve.prenom ?? ''}, ${formatDateHeure(seances[0]?.debut ?? '')}`
            }
            ouverteParDefaut={false}
            cleMemoire="manuel.eleves.seances"
          >
            {seances.length === 0 ? (
              <p className="note-line">Aucune séance planifiée.</p>
            ) : (
              <div className="list">
                {seances.map((s) => (
                  <div key={`${s.eleve.id}-${s.debut}`} className="li">
                    <span className="li-main">
                      <span className="li-title">
                        {s.eleve.prenom} — {formatDateHeure(s.debut)}
                      </span>
                      <span className="li-sub">
                        {s.dureeMin} min · {s.motif}
                      </span>
                    </span>
                    <Link
                      className="btn btn--ghost btn--sm"
                      to={adressesManuelEleve.preparation(s.eleve.id)}
                    >
                      Préparer
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </SectionRabattable>
        </div>

        <div data-testid="eleves-urgence-ajout">
          <SectionRabattable
            titre="Ajouter un élève"
            resume="Par le code de suivi qu’il vous donne"
            ouverteParDefaut={reponse.eleves.length === 0}
          >
            {ajout}
          </SectionRabattable>
        </div>

        <div data-testid="eleves-urgence-ordre">
          <SectionRabattable
            titre="Comment l’ordre est établi"
            resume="La règle, en clair"
            ouverteParDefaut={false}
            cleMemoire="manuel.eleves.ordre"
          >
            <p className="note-line">{reponse.explication}</p>
          </SectionRabattable>
        </div>
      </div>
    </>
  );
}

function FiltreDeSignal({
  libelle,
  eleves,
  actif,
  surChoix,
  code,
}: {
  readonly libelle: string;
  readonly eleves: number;
  readonly actif: boolean;
  readonly surChoix: () => void;
  readonly code: SignalDePriorite | null;
}) {
  return (
    <button
      type="button"
      className={actif ? 'chip-filter is-on' : 'chip-filter'}
      aria-pressed={actif}
      onClick={surChoix}
      data-testid={`eleves-urgence-filtre-${code ?? 'tous'}`}
    >
      {libelle} <span className="rail__cnt">{eleves}</span>
    </button>
  );
}

function LigneSuivie({
  ligne,
}: {
  readonly ligne: Extract<LigneDeListe, { readonly suivi: true }>;
}) {
  const e = ligne.donnees;
  const fiche = adressesManuelEleve.fiche(e.eleve.id);
  const navigate = useNavigate();
  const septJours = direSeptJours(e.septDerniersJours);
  const seance =
    e.prochaineSeance === null
      ? 'Aucune séance planifiée'
      : `Séance le ${formatDateHeure(e.prochaineSeance.debut)}`;
  return (
    <tr
      className="ligne-cliquable"
      data-testid="eleves-urgence-ligne"
      onClick={(evenement) => {
        /* Un lien ou un bouton de la ligne garde son propre geste. */
        if ((evenement.target as HTMLElement).closest('a, button') !== null) return;
        navigate(fiche);
      }}
    >
      <td>
        <span className="data">{e.ordre}</span>
        {e.aVoirEnPriorite ? <span className="cell-sub">À voir en priorité</span> : null}
      </td>
      <th scope="row">
        <span className="cell-eleve">
          <span className="avatar-sq avatar-sq--sm" aria-hidden="true">
            {initiale(e.eleve.prenom)}
          </span>
          <span>
            <Link to={fiche}>{e.eleve.prenom}</Link>
            <span className="cell-sub">
              {e.chapitre === null ? 'Objectif à définir' : e.chapitre.titre}
            </span>
          </span>
        </span>
      </th>
      <td className="cellule-raison">
        <span data-testid="eleves-urgence-raison">{e.pourquoiMaintenant}</span>
        {e.signal === null && e.autresSignaux.length === 0 ? null : (
          <span className="cell-sub">
            {e.signal === null ? null : (
              <span className="tag tag--acc" data-testid="eleves-urgence-signal">
                {e.signal.etiquette}
              </span>
            )}
            {e.autresSignaux.map((s) => (
              <span key={s.code} className="tag tag--suite" title={s.phrase}>
                {s.etiquette}
              </span>
            ))}
          </span>
        )}
        {/* Sous 1 200 px, les deux dernières colonnes en une ligne (`UX-7-11`). */}
        <span className="cell-sub cellule-raison__repli" data-testid="eleves-urgence-repli">
          Sept jours : {septJours.toLocaleLowerCase('fr')} · {seance}
        </span>
      </td>
      <td className="cellule-evaluation">
        {e.prochaineEvaluation === null ? (
          <span className="cell-sub">
            {e.chapitre === null ? 'Objectif à définir' : 'Aucune à venir'}
          </span>
        ) : (
          <>
            {e.prochaineEvaluation.evaluation.titre}
            <span className="cell-sub">
              {jourCivil(e.prochaineEvaluation.evaluation.date)} ·{' '}
              {dansCombien(e.prochaineEvaluation.evaluation.joursRestants)}
            </span>
            <Badge
              tone={TON_PREPARATION[e.prochaineEvaluation.etat]}
              label={e.prochaineEvaluation.libelleEtat}
            />
          </>
        )}
      </td>
      <td className="col-large">
        {septJours}
        <span className="cell-sub">
          {e.dernierTravailLe === null
            ? 'Aucun travail corrigé'
            : `Dernier travail le ${direJour(e.dernierTravailLe)}`}
        </span>
        {e.sequence === null ? null : (
          <span className="cell-sub">
            Séquence : {e.sequence.faits} sur {e.sequence.total} faits
          </span>
        )}
      </td>
      <td className="col-large">
        {e.prochaineSeance === null ? (
          <span className="cell-sub">Aucune séance planifiée</span>
        ) : (
          <>
            {formatDateHeure(e.prochaineSeance.debut)}
            <span className="cell-sub">{e.prochaineSeance.dureeMin} min</span>
          </>
        )}
      </td>
      <td className="cellule-fiche">
        <Link className="btn btn--ghost btn--sm" to={fiche} data-testid="eleves-urgence-fiche">
          Fiche
        </Link>
      </td>
    </tr>
  );
}

function LigneSansSuivi({
  ligne,
}: {
  readonly ligne: Extract<LigneDeListe, { readonly suivi: false }>;
}) {
  return (
    <tr data-testid="eleves-urgence-ligne-sans-suivi">
      <td>
        {ligne.ordre === null ? (
          <span className="cell-sub">—</span>
        ) : (
          <span className="data">{ligne.ordre}</span>
        )}
      </td>
      <th scope="row">
        <span className="cell-eleve">
          <span className="avatar-sq avatar-sq--sm" aria-hidden="true">
            {initiale(ligne.eleve.prenom)}
          </span>
          <span>{ligne.eleve.prenom}</span>
        </span>
      </th>
      {/* Trois colonnes toujours présentes, puis les deux qui se replient sous 1 200 px. */}
      <td colSpan={3}>
        <span className="cell-sub">{PHRASE_SUIVI_NON_AUTORISE}</span>
      </td>
      <td colSpan={2} className="col-large"></td>
    </tr>
  );
}
